import { Btn } from '@vm/shared';

export interface Bindings {
  forward: string[];
  back: string[];
  left: string[];
  right: string[];
  jump: string[];
  reload: string[];
  gadget: string[];
  use: string[];
  lastWeapon: string[];
  scoreboard: string[];
  chat: string[];
  /** Primary fire and aim: keys or mouse buttons (`Mouse0` left, `Mouse1` middle, `Mouse2` right, `Mouse3`/`Mouse4` side buttons). */
  fire: string[];
  aim: string[];
  slots: string[][];
}

/** Rebindable actions (primary key per action; defaults keep their secondary keys). */
export type BindAction =
  | 'forward' | 'back' | 'left' | 'right' | 'jump' | 'reload' | 'gadget' | 'use' | 'lastWeapon' | 'scoreboard' | 'chat' | 'fire' | 'aim'
  | 'slot1' | 'slot2' | 'slot3' | 'slot4' | 'slot5' | 'slot6';

export const BIND_ACTIONS: { action: BindAction; label: string }[] = [
  { action: 'fire', label: 'Shoot' },
  { action: 'aim', label: 'Aim' },
  { action: 'forward', label: 'Move forward' },
  { action: 'back', label: 'Move back' },
  { action: 'left', label: 'Strafe left' },
  { action: 'right', label: 'Strafe right' },
  { action: 'jump', label: 'Jump / jetpack' },
  { action: 'reload', label: 'Reload' },
  { action: 'gadget', label: 'Gadget' },
  { action: 'use', label: 'Use / pick up' },
  { action: 'lastWeapon', label: 'Last weapon' },
  { action: 'slot1', label: 'Primary' },
  { action: 'slot2', label: 'Backup' },
  { action: 'slot3', label: 'Melee' },
  { action: 'slot4', label: 'Heavy' },
  { action: 'slot5', label: 'Sniper' },
  { action: 'slot6', label: 'Build tool' },
  { action: 'scoreboard', label: 'Scoreboard' },
  { action: 'chat', label: 'Chat' },
];

export const DEFAULT_BINDINGS: Bindings = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  reload: ['KeyR'],
  gadget: ['KeyG', 'KeyF'],
  use: ['KeyE'],
  lastWeapon: ['KeyQ'],
  scoreboard: ['Tab'],
  chat: ['KeyT', 'Enter'],
  fire: ['Mouse0'],
  aim: ['Mouse2'],
  slots: [['Digit1'], ['Digit2'], ['Digit3'], ['Digit4'], ['Digit5'], ['Digit6', 'KeyB']],
};

/** Codes currently bound to an action. */
export function actionCodes(b: Bindings, action: BindAction): string[] {
  if (action.startsWith('slot')) return b.slots[Number(action.slice(4)) - 1];
  return b[action as Exclude<BindAction, `slot${number}`>];
}

/**
 * Applies user overrides: each override becomes the action's only key, and that key is removed
 * from every other action so one key never does two things.
 */
export function resolveBindings(overrides: Partial<Record<BindAction, string>> | undefined): Bindings {
  const b: Bindings = JSON.parse(JSON.stringify(DEFAULT_BINDINGS));
  if (!overrides) return b;
  const all = BIND_ACTIONS.map((a) => a.action);
  for (const [action, code] of Object.entries(overrides) as [BindAction, string][]) {
    if (!code || !all.includes(action)) continue;
    for (const other of all) {
      const list = actionCodes(b, other);
      const i = list.indexOf(code);
      if (i >= 0) list.splice(i, 1);
    }
    const list = actionCodes(b, action);
    list.splice(0, list.length, code);
  }
  return b;
}

/** The code that an action shows and uses as its main key (first bound code), or '' when none. */
export function primaryCode(b: Bindings, action: BindAction): string {
  return actionCodes(b, action)[0] ?? '';
}

/**
 * Sets `action` to `code`. When another action already uses that code the two SWAP (the other action gets this action's old key),
 * so rebinding never silently leaves an action without a key. Returns the new overrides and the action that was swapped, if any.
 * Overrides always hold one key per action; defaults that carry a second key (Arrow keys, F for gadget) lose it only when it is
 * the key being taken.
 */
export function rebind(
  overrides: Partial<Record<BindAction, string>> | undefined,
  action: BindAction,
  code: string,
): { overrides: Partial<Record<BindAction, string>>; swapped: BindAction | null } {
  const current = resolveBindings(overrides);
  const next: Partial<Record<BindAction, string>> = { ...(overrides ?? {}) };
  let swapped: BindAction | null = null;
  const mine = primaryCode(current, action);
  for (const other of BIND_ACTIONS.map((a) => a.action)) {
    if (other === action) continue;
    if (actionCodes(current, other).includes(code)) {
      swapped = other;
      // The other action takes this action's old key; if this action had none, the other keeps its remaining keys unbound.
      if (mine && mine !== code) next[other] = mine;
      else delete next[other];
      break;
    }
  }
  next[action] = code;
  // A swap can hand the displaced action a key that a third action also lists as a secondary default: resolveBindings strips it there.
  return { overrides: next, swapped };
}

/**
 * Puts one action back on its default key(s). If another action has since taken that key, it is a swap (that action gets this
 * action's current key), exactly like rebinding, so no action is left without a key. The result drops any override that just
 * restates a default, so "changed" markers and the stored settings stay minimal.
 */
export function resetBinding(
  overrides: Partial<Record<BindAction, string>> | undefined,
  action: BindAction,
): { overrides: Partial<Record<BindAction, string>>; swapped: BindAction | null } {
  const def = actionCodes(DEFAULT_BINDINGS, action)[0];
  if (!def) {
    const next = { ...(overrides ?? {}) };
    delete next[action];
    return { overrides: next, swapped: null };
  }
  const r = rebind(overrides, action, def);
  // This action is back on its default primary key: that needs no override (and restores any secondary default key).
  delete r.overrides[action];
  for (const a of BIND_ACTIONS.map((x) => x.action)) {
    if (r.overrides[a] && actionCodes(DEFAULT_BINDINGS, a).length === 1 && actionCodes(DEFAULT_BINDINGS, a)[0] === r.overrides[a]) delete r.overrides[a];
  }
  return r;
}

/** Friendly text for a key or mouse-button code. */
export function codeLabel(code: string): string {
  if (code.startsWith('Mouse')) {
    const n = Number(code.slice(5));
    return ['Left click', 'Middle click', 'Right click', 'Mouse 4', 'Mouse 5'][n] ?? `Mouse ${n + 1}`;
  }
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = { ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Space: 'Space', Tab: 'Tab', Enter: 'Enter', Escape: 'Esc', ShiftLeft: 'Shift', ShiftRight: 'Shift (R)', ControlLeft: 'Ctrl', ControlRight: 'Ctrl (R)', AltLeft: 'Alt', AltRight: 'Alt (R)', Backquote: '`' };
  return map[code] ?? code;
}

/** Code for a DOM mouse button index. */
export const mouseCode = (button: number): string => `Mouse${button}`;

/** Subset of Element.requestPointerLock that this needs (the options argument is not in every DOM typing). */
type LockFn = (options?: { unadjustedMovement?: boolean }) => Promise<void> | void;

/**
 * Locks the pointer, asking for unadjusted (no operating-system acceleration) mouse movement when `raw` is set. Browsers that do
 * not support the option reject (or throw); then it retries once with a plain lock so the game always locks. Returns which kind of
 * lock was requested last ('raw' | 'plain'), for tests.
 */
export function lockPointer(el: { requestPointerLock?: LockFn }, raw: boolean): 'raw' | 'plain' {
  const plain = (): 'plain' => {
    try {
      (el.requestPointerLock?.() as Promise<void> | undefined)?.catch?.(() => {});
    } catch {
      /* the browser refused: nothing more to try */
    }
    return 'plain';
  };
  if (!raw) return plain();
  try {
    const p = el.requestPointerLock?.({ unadjustedMovement: true }) as Promise<void> | undefined;
    p?.catch?.(() => {
      plain();
    });
  } catch {
    return plain();
  }
  return 'raw';
}

/**
 * Collects raw input from keyboard, mouse (pointer lock), gamepad and the touch overlay.
 * The game samples it once per 60 Hz frame.
 */
export class Input {
  readonly keys = new Set<string>();
  /** Accumulated look delta in pixels since the last sample. */
  lookX = 0;
  lookY = 0;
  mouseButtons = 0;
  wheel = 0;
  /** Touch/gamepad analog movement (-1..1) and extra buttons. */
  touchMoveX = 0;
  touchMoveZ = 0;
  touchButtons = 0;
  /** Slot requested by key/wheel/touch; -1 = unchanged. */
  slotRequest = -1;
  lastWeaponRequest = false;
  onScoreboard?: (open: boolean) => void;
  onChat?: () => void;
  onMenu?: () => void;
  enabled = true;
  bindings: Bindings = DEFAULT_BINDINGS;
  private gamepadPrev = 0;
  private cleanup: (() => void)[] = [];

  constructor(private canvas: HTMLElement) {
    const on = <K extends keyof WindowEventMap>(t: K, fn: (e: WindowEventMap[K]) => void, target: Window | HTMLElement | Document = window): void => {
      target.addEventListener(t, fn as EventListener, { passive: false });
      this.cleanup.push(() => target.removeEventListener(t, fn as EventListener));
    };
    on('keydown', (e) => this.keyDown(e));
    on('keyup', (e) => {
      this.keys.delete(e.code);
      this.setLogical(e.code, false);
      if (this.bindings.scoreboard.includes(e.code)) this.onScoreboard?.(false);
    });
    on('blur', () => {
      this.keys.clear();
      this.mouseButtons = 0;
    });
    on('mousemove', (e) => {
      if (document.pointerLockElement === this.canvas) {
        this.lookX += e.movementX;
        this.lookY += e.movementY;
      }
    });
    on('mousedown', (e) => {
      if (document.pointerLockElement !== this.canvas) return;
      // Physical buttons go through the bindings: `mouseButtons` holds the LOGICAL buttons (1 = fire, 4 = aim).
      this.setLogical(mouseCode(e.button), true);
      e.preventDefault();
    });
    on('mouseup', (e) => {
      this.setLogical(mouseCode(e.button), false);
    });
    on('wheel', (e) => {
      if (document.pointerLockElement === this.canvas) this.wheel += Math.sign(e.deltaY);
    });
    on('contextmenu', (e) => e.preventDefault(), canvas);
  }

  private keyDown(e: KeyboardEvent): void {
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    if (e.code === 'Escape') {
      this.onMenu?.();
      return;
    }
    if (!this.enabled) return;
    const b = this.bindings;
    if (b.scoreboard.includes(e.code)) {
      e.preventDefault();
      this.onScoreboard?.(true);
    }
    if (b.chat.includes(e.code) && !e.repeat) {
      e.preventDefault();
      this.onChat?.();
      return;
    }
    b.slots.forEach((codes, i) => {
      if (codes.includes(e.code)) this.slotRequest = i;
    });
    if (b.lastWeapon.includes(e.code) && !e.repeat) this.lastWeaponRequest = true;
    if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
    this.keys.add(e.code);
    this.setLogical(e.code, true);
  }

  /** Presses or releases the logical fire/aim button that `code` (a key or `MouseN`) is bound to. */
  private setLogical(code: string, down: boolean): void {
    const b = this.bindings;
    if (b.fire.includes(code)) this.mouseButtons = down ? this.mouseButtons | 1 : this.mouseButtons & ~1;
    if (b.aim.includes(code)) this.mouseButtons = down ? this.mouseButtons | 4 : this.mouseButtons & ~4;
  }

  /** Ask the browser for unaccelerated (raw) mouse movement when locking the pointer. Live: applies on the next lock. */
  rawMouse = true;

  requestPointerLock(): void {
    if (document.pointerLockElement !== this.canvas) lockPointer(this.canvas, this.rawMouse);
  }

  get locked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  private held(codes: string[]): boolean {
    for (const c of codes) if (this.keys.has(c)) return true;
    return false;
  }

  /** Samples movement axes and button bits for one frame. */
  sample(): { mx: number; mz: number; buttons: number } {
    let mx = 0;
    let mz = 0;
    let buttons = 0;
    if (this.enabled) {
      const b = this.bindings;
      if (this.held(b.forward)) mz += 1;
      if (this.held(b.back)) mz -= 1;
      if (this.held(b.right)) mx += 1;
      if (this.held(b.left)) mx -= 1;
      if (this.held(b.jump)) buttons |= Btn.Jump | Btn.Jet;
      if (this.held(b.reload)) buttons |= Btn.Reload;
      if (this.held(b.gadget)) buttons |= Btn.Gadget;
      if (this.held(b.use)) buttons |= Btn.Use;
      if (this.mouseButtons & 1) buttons |= Btn.Fire;
      if (this.mouseButtons & 4) buttons |= Btn.Aim;
      mx += this.touchMoveX;
      mz += this.touchMoveZ;
      buttons |= this.touchButtons;
      this.pollGamepad((gx, gz, gb) => {
        mx += gx;
        mz += gz;
        buttons |= gb;
      });
    }
    const len = Math.hypot(mx, mz);
    if (len > 1) {
      mx /= len;
      mz /= len;
    }
    return { mx, mz, buttons };
  }

  private pollGamepad(cb: (mx: number, mz: number, buttons: number) => void): void {
    const pads = navigator.getGamepads?.();
    if (!pads) return;
    for (const gp of pads) {
      if (!gp || !gp.connected) continue;
      const dz = (v: number): number => (Math.abs(v) < 0.18 ? 0 : v);
      const mx = dz(gp.axes[0] ?? 0);
      const mz = -dz(gp.axes[1] ?? 0);
      this.lookX += dz(gp.axes[2] ?? 0) * 14;
      this.lookY += dz(gp.axes[3] ?? 0) * 10;
      const pressed = (i: number): boolean => !!gp.buttons[i]?.pressed;
      let b = 0;
      if (pressed(7)) b |= Btn.Fire;
      if (pressed(6)) b |= Btn.Aim;
      if (pressed(0)) b |= Btn.Jump | Btn.Jet;
      if (pressed(2)) b |= Btn.Reload;
      if (pressed(5) || pressed(1)) b |= Btn.Gadget;
      const bits = gp.buttons.reduce((acc, bt, i) => acc | (bt.pressed ? 1 << i : 0), 0);
      const edge = bits & ~this.gamepadPrev;
      this.gamepadPrev = bits;
      if (edge & (1 << 3)) this.wheel += 1;
      if (edge & (1 << 4)) this.wheel -= 1;
      if (edge & (1 << 12)) this.slotRequest = 2;
      cb(mx, mz, b);
      return;
    }
  }

  takeLook(): [number, number] {
    const l: [number, number] = [this.lookX, this.lookY];
    this.lookX = 0;
    this.lookY = 0;
    return l;
  }

  dispose(): void {
    for (const c of this.cleanup) c();
  }
}
